import React, { useState, useEffect, useLayoutEffect } from 'react';
import authService from './services/authService';
import { createBrowserRouter, RouterProvider, Outlet, Navigate, useMatches, useNavigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';



// Bootstrap CSS
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

// Perfect Scrollbar CSS
import 'react-perfect-scrollbar/dist/css/styles.css';

// Custom styles
import './styles/main.scss';


// Layout Components
import Header from './components/layout/Header';
import AccountHeader from './components/layout/AccountHeader';
import Footer from './components/layout/Footer';
import { setProductModalHandler } from './utils/modalHelpers';
import ProductDetailModal from './components/products/ProductDetailModal';
import MedRecFilterBar from './components/common/MedRecFilterBar';
import { MedRecFilterProvider } from './context/MedRecFilterContext';

// Auth Components
import ProtectedRoute from './components/auth/ProtectedRoute';
import useAuth from './hooks/useAuth';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import CategoryOverviewPage from './pages/products/CategoryOverviewPage';
import CategoryPage from './pages/products/CategoryPage';
import ProductPage from './pages/products/ProductPage';
import ProductsPage from './pages/products/ProductsPage';
import BrandOverviewPage from './pages/brands/BrandOverviewPage';
import BrandPage from './pages/brands/BrandPage';
import NewArrivalsPage from './pages/products/NewArrivalsPage';
import LabReportsPage from './pages/products/LabReportsPage';
import QuickOrderPage from './pages/cart/QuickOrderPage';
import CartPage from './pages/cart/CartPage';
import BackOrderHistoryPage from './pages/backorder/BackOrderHistoryPage';
import DashboardPage from './pages/account/DashboardPage';
import OrderHistoryPage from './pages/account/OrderHistoryPage';
import AccountOrdersPage from './pages/account/AccountOrdersPage';
import AccountBackOrdersPage from './pages/account/AccountBackOrdersPage';
import CreditsPage from './pages/account/CreditsPage';
import CreditsAlertBar from './components/common/CreditsAlertBar';
import ProfilePage from './pages/account/ProfilePage';
import NotificationPreferencesPage from './pages/account/NotificationPreferencesPage';
import NotFoundPage from './pages/NotFoundPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import DisclaimerPage from './pages/DisclaimerPage';
import SupportPage from './pages/SupportPage';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
    mutations: {
      retry: 1,
    }
  },
});

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Application error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="alert alert-danger">
                <h4>Something went wrong</h4>
                <p>The application encountered an error. Please refresh the page to try again.</p>
                <button 
                  className="btn btn-primary" 
                  onClick={() => window.location.reload()}
                >
                  Refresh Page
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Routes brand users to ProductsPage, everyone else to HomePage
 */
const HomeRouter = () => {
  const { isBrandUser } = useAuth();
  return isBrandUser() ? <ProductsPage /> : <HomePage />;
};

/**
 * Layout wrapper component that reads route metadata to determine which header to show
 */
const LayoutWrapper = () => {
  const matches = useMatches();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedProductId, setSelectedProductId] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  // Listen for API version mismatch
  useEffect(() => {
    const handler = () => setUpdateAvailable(true);
    window.addEventListener('app-version-update', handler);
    return () => window.removeEventListener('app-version-update', handler);
  }, []);

  // The shop and the admin portal share one auth cookie. When this tab comes back into view, check
  // whether someone else signed in meanwhile (e.g. an employee in the admin portal); if so, reload
  // as that user rather than keep showing the old one while every request acts as the new one.
  useEffect(() => {
    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      if (await authService.checkSignedInUserChanged()) window.location.reload();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // Get the deepest route match to determine layout
  const currentMatch = matches[matches.length - 1];
  const layoutType = currentMatch?.handle?.layout || 'default';

  // Scroll to top on route change (useLayoutEffect runs before paint)
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Set up global modal handler once
  useEffect(() => {
    setProductModalHandler((productId) => {
      setSelectedProductId(productId);
      setShowProductModal(true);
    });
  }, []);

  // Modal close handler
  const handleCloseModal = () => {
    setShowProductModal(false);
    setSelectedProductId(null);
  };

  // Full page handler using navigate
  const handleOpenFullPage = (productId) => {
    handleCloseModal();
    navigate(`/products/${productId}`);
  };

  // Determine which header to render based on layout type
  const getHeader = () => {
    switch (layoutType) {
      case 'account':
        return <AccountHeader />;
      case 'auth':
        return null; // No header for login/register pages
      case 'default':
      default:
        return <Header />;
    }
  };

  useEffect(() => {
    let lastScrollY = 0;
    let isScrolled = false;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const shouldBeScrolled = currentScrollY > 50;
      
      if (shouldBeScrolled !== isScrolled) {
        isScrolled = shouldBeScrolled;
        
        if (isScrolled) {
          document.body.classList.add('scrolled');
        } else {
          document.body.classList.remove('scrolled');
        }
      }
      
      lastScrollY = currentScrollY;
    };

    let scrollTimeout;
    const throttledHandleScroll = () => {
      if (scrollTimeout) return;
      
      scrollTimeout = setTimeout(() => {
        handleScroll();
        scrollTimeout = null;
      }, 10);
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    handleScroll(); // Initialize

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, []);
  
  return (
    <>
      <div className="App min-vh-100 d-flex flex-column">
        {updateAvailable && (
          <div className="bg-danger text-white text-center py-2 px-3" style={{ zIndex: 1060 }}>
            <strong className="me-1">Update Available</strong>
            <span className="me-2">— A newer version is available. Please update to the latest version for the best experience.</span>
            <button
              className="btn btn-sm btn-light"
              onClick={() => {
                localStorage.removeItem('auth_token');
                localStorage.removeItem('auth_user');
                localStorage.removeItem('current_company');
                localStorage.removeItem('current_store');
                window.location.href = '/login';
              }}
            >
              Update
            </button>
          </div>
        )}
        {layoutType !== 'auth' && <CreditsAlertBar />}
        {getHeader()}

        <main className="flex-grow-1">
          <Outlet />
        </main>
        
        <Footer />
      </div>

      {/* Global Product Modal */}
      <ProductDetailModal
        productId={selectedProductId}
        isOpen={showProductModal}
        onClose={handleCloseModal}
        onOpenFullPage={handleOpenFullPage}
      />

      {/* Med/Rec Filter Bar - only shows for stores with both med and rec */}
      <MedRecFilterBar />
    </>
  );
};

/**
 * Router configuration with layout metadata
 */
const router = createBrowserRouter([
  {
    path: '/',
    element: <LayoutWrapper />,
    children: [
      // Auth routes (no header)
      {
        path: 'login',
        element: <LoginPage />,
        handle: { layout: 'auth' }
      },
      {
        path: 'register',
        element: <RegisterPage />,
        handle: { layout: 'auth' }
      },
      {
        path: 'reset-password',
        element: <ResetPasswordPage />,
        handle: { layout: 'auth' }
      },
      {
        path: 'forgot-password',
        element: <ForgotPasswordPage />,
        handle: { layout: 'auth' }
      },
      
      // Account routes (account header)
      {
        path: 'account',
        element: (
          <ProtectedRoute requireStore={false}>
            <DashboardPage />
          </ProtectedRoute>
        ),
        handle: { layout: 'account' }
      },
      {
        path: 'account/orders',
        element: (
          <ProtectedRoute requireStore={false} customerOnly={true}>
            <AccountOrdersPage />
          </ProtectedRoute>
        ),
        handle: { layout: 'account' }
      },
      {
        path: 'account/back-orders',
        element: (
          <ProtectedRoute requireStore={false} customerOnly={true}>
            <AccountBackOrdersPage />
          </ProtectedRoute>
        ),
        handle: { layout: 'account' }
      },
      {
        // Credits belong to a store, so this needs one selected (unlike the all-stores pages above).
        path: 'account/credits',
        element: (
          <ProtectedRoute requireStore={true} customerOnly={true}>
            <CreditsPage />
          </ProtectedRoute>
        ),
        handle: { layout: 'account' }
      },
      {
        path: 'account/profile',
        element: (
          <ProtectedRoute requireStore={false} customerOnly={true}>
            <ProfilePage />
          </ProtectedRoute>
        ),
        handle: { layout: 'account' }
      },
      {
        path: 'account/notifications',
        element: (
          <ProtectedRoute requireStore={false} customerOnly={true}>
            <NotificationPreferencesPage />
          </ProtectedRoute>
        ),
        handle: { layout: 'account' }
      },

      // Shopping routes (default header with full navigation)
      {
        index: true,
        element: (
          <ProtectedRoute requireStore={true}>
            <HomeRouter />
          </ProtectedRoute>
        )
        // No handle needed - defaults to 'default'
      },
      {
        path: 'categories',
        element: (
          <ProtectedRoute requireStore={true}>
            <CategoryOverviewPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'categories/:slug',
        element: (
          <ProtectedRoute requireStore={true}>
            <CategoryPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'brands',
        element: (
          <ProtectedRoute requireStore={true}>
            <BrandOverviewPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'brands/:slug',
        element: (
          <ProtectedRoute requireStore={true}>
            <BrandPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'new-arrivals',
        element: (
          <ProtectedRoute requireStore={true}>
            <NewArrivalsPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'lab-reports',
        element: (
          <ProtectedRoute requireStore={true}>
            <LabReportsPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'products',
        element: (
          <ProtectedRoute requireStore={true}>
            <ProductsPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'products/:productId',
        element: (
          <ProtectedRoute requireStore={true}>
            <ProductPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'quick-order',
        element: (
          <ProtectedRoute requireStore={true} customerOnly={true}>
            <QuickOrderPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'checkout',
        element: (
          <ProtectedRoute requireStore={true} customerOnly={true}>
            <CartPage />
          </ProtectedRoute>
        )
      },
      {
        path: 'back-orders',
        element: (
          <ProtectedRoute requireStore={true} customerOnly={true}>
            <BackOrderHistoryPage />
          </ProtectedRoute>
        )
      },
      {
        // Old singular route now points at the new back-orders grid.
        path: 'back-order',
        element: <Navigate to="/back-orders" replace />
      },
      {
        path: 'orders',
        element: (
          <ProtectedRoute requireStore={true} customerOnly={true}>
            <OrderHistoryPage />
          </ProtectedRoute>
        )
      },

      // Legal/Support pages (public)
      {
        path: 'privacy-policy',
        element: <PrivacyPolicyPage />
      },
      {
        path: 'disclaimer',
        element: <DisclaimerPage />
      },
      {
        path: 'support',
        element: <SupportPage />
      },

      // Error routes
      {
        path: '404',
        element: <NotFoundPage />
      },
      {
        path: '*',
        element: <NotFoundPage />
      }
    ]
  }
]);

/**
 * Main App Component
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <MedRecFilterProvider>
        <ErrorBoundary>
          <RouterProvider router={router} />
        </ErrorBoundary>
      </MedRecFilterProvider>
    </QueryClientProvider>
  );
}

export default App;